import React, { useEffect, useState } from "react";
import axios from "axios";
import {
  Newspaper, Plus, Save, Trash2, Eye, EyeOff, ExternalLink, ArrowLeft, X,
} from "lucide-react";
import ReactQuill from "react-quill-new";
import "react-quill-new/dist/quill.snow.css";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { useToast } from "../hooks/use-toast";

const API = process.env.REACT_APP_BACKEND_URL;

// Toolbar Quill: heading, bold/italic/underline, link, immagine, liste, blockquote, allineamento, clean
const QUILL_MODULES = {
  toolbar: [
    [{ header: [2, 3, false] }],
    ["bold", "italic", "underline", "strike"],
    [{ list: "ordered" }, { list: "bullet" }],
    ["blockquote", "code-block"],
    [{ align: [] }],
    ["link", "image"],
    ["clean"],
  ],
  clipboard: { matchVisual: false },
};
const QUILL_FORMATS = [
  "header", "bold", "italic", "underline", "strike",
  "list", "blockquote", "code-block",
  "align", "link", "image",
];

export default function BlogAdminSection() {
  const [articoli, setArticoli] = useState([]);
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // articolo in editing o "new"
  const { toast } = useToast();

  const load = () => {
    setLoading(true);
    axios
      .get(`${API}/api/admin/blog`, { withCredentials: true })
      .then((r) => setArticoli(r.data || []))
      .catch(() => setArticoli([]))
      .finally(() => setLoading(false));
  };

  useEffect(() => {
    load();
  }, []);

  const togglePublish = async (a) => {
    const url = a.stato === "pubblicato" ? "unpublish" : "publish";
    try {
      await axios.post(
        `${API}/api/admin/blog/${a.id}/${url}`,
        {},
        { withCredentials: true }
      );
      toast({
        description: a.stato === "pubblicato" ? "Articolo tornato in bozza." : "Articolo pubblicato.",
      });
      load();
    } catch (e) {
      toast({ description: "Errore: " + (e.response?.data?.detail || e.message), variant: "destructive" });
    }
  };

  const handleDelete = async (a) => {
    if (!window.confirm(`Eliminare definitivamente "${a.titolo}"?`)) return;
    try {
      await axios.delete(`${API}/api/admin/blog/${a.id}`, { withCredentials: true });
      toast({ description: "Articolo eliminato." });
      load();
    } catch (e) {
      toast({ description: "Errore: " + (e.response?.data?.detail || e.message), variant: "destructive" });
    }
  };

  if (editing) {
    return (
      <BlogEditor
        articolo={editing === "new" ? null : editing}
        onBack={() => setEditing(null)}
        onSaved={() => {
          setEditing(null);
          load();
        }}
      />
    );
  }

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <div>
          <h2 className="text-2xl font-extrabold text-white flex items-center gap-2">
            <Newspaper className="h-6 w-6 text-[#f59e0b]" /> Blog & News
          </h2>
          <p className="text-sm text-slate-400 mt-1">
            Gestisci articoli pubblici SEO-friendly. Bozze non compaiono sul sito.
          </p>
        </div>
        <Button
          data-testid="blog-admin-new-btn"
          onClick={() => setEditing("new")}
          className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"
        >
          <Plus className="mr-2 h-4 w-4" /> Nuovo articolo
        </Button>
      </div>

      {loading ? (
        <div className="text-slate-400 py-8 text-center">Caricamento…</div>
      ) : articoli.length === 0 ? (
        <div className="rounded-2xl border border-dashed border-white/20 p-10 text-center text-slate-400">
          Nessun articolo. Clicca &quot;Nuovo articolo&quot; per iniziare.
        </div>
      ) : (
        <div className="rounded-2xl bg-white/5 border border-white/10 overflow-hidden" data-testid="blog-admin-list">
          <table className="w-full text-sm">
            <thead className="bg-white/5 text-slate-300 uppercase text-[10.5px] tracking-widest">
              <tr>
                <th className="px-4 py-3 text-left">Titolo</th>
                <th className="px-4 py-3 text-left">Categoria</th>
                <th className="px-4 py-3 text-left">Stato</th>
                <th className="px-4 py-3 text-left">Aggiornato</th>
                <th className="px-4 py-3 text-right">Azioni</th>
              </tr>
            </thead>
            <tbody className="divide-y divide-white/5 text-slate-200">
              {articoli.map((a) => (
                <tr key={a.id} className="hover:bg-white/5">
                  <td className="px-4 py-3 max-w-md">
                    <div className="font-bold text-white truncate">{a.titolo}</div>
                    <div className="text-[11px] text-slate-400 truncate">/{a.slug}</div>
                  </td>
                  <td className="px-4 py-3 text-slate-300">{a.categoria || "—"}</td>
                  <td className="px-4 py-3">
                    {a.stato === "pubblicato" ? (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-emerald-500/20 text-emerald-300 text-[10.5px] font-bold">
                        <Eye className="h-3 w-3" /> Pubblicato
                      </span>
                    ) : (
                      <span className="inline-flex items-center gap-1 px-2 py-1 rounded-full bg-slate-500/20 text-slate-300 text-[10.5px] font-bold">
                        <EyeOff className="h-3 w-3" /> Bozza
                      </span>
                    )}
                  </td>
                  <td className="px-4 py-3 text-slate-400 text-[12px]">
                    {a.updated_at ? new Date(a.updated_at).toLocaleString("it-IT") : "—"}
                  </td>
                  <td className="px-4 py-3 text-right">
                    <div className="inline-flex items-center gap-1">
                      {a.stato === "pubblicato" && (
                        <a
                          href={`/blog/${a.slug}`}
                          target="_blank"
                          rel="noreferrer"
                          className="p-1.5 rounded-md hover:bg-white/10 text-slate-300"
                          title="Apri articolo pubblico"
                          data-testid={`blog-admin-view-${a.slug}`}
                        >
                          <ExternalLink className="h-4 w-4" />
                        </a>
                      )}
                      <button
                        onClick={() => togglePublish(a)}
                        className="px-2 py-1 rounded-md bg-white/5 hover:bg-white/15 text-white text-xs font-semibold"
                        data-testid={`blog-admin-toggle-${a.id}`}
                      >
                        {a.stato === "pubblicato" ? "Ritira" : "Pubblica"}
                      </button>
                      <button
                        onClick={() => setEditing(a)}
                        className="px-2 py-1 rounded-md bg-[#f59e0b]/90 hover:bg-[#f59e0b] text-[#0a1020] text-xs font-bold"
                        data-testid={`blog-admin-edit-${a.id}`}
                      >
                        Modifica
                      </button>
                      <button
                        onClick={() => handleDelete(a)}
                        className="p-1.5 rounded-md hover:bg-red-500/20 text-red-400"
                        title="Elimina"
                        data-testid={`blog-admin-delete-${a.id}`}
                      >
                        <Trash2 className="h-4 w-4" />
                      </button>
                    </div>
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </div>
      )}
    </div>
  );
}

function BlogEditor({ articolo, onBack, onSaved }) {
  const isNew = !articolo;
  const [form, setForm] = useState({
    titolo: articolo?.titolo || "",
    sottotitolo: articolo?.sottotitolo || "",
    slug: articolo?.slug || "",
    excerpt: articolo?.excerpt || "",
    contenuto: articolo?.contenuto || "",
    cover_url: articolo?.cover_url || "",
    autore: articolo?.autore || "Redazione A.C.D.T.",
    categoria: articolo?.categoria || "News",
    tags: (articolo?.tags || []).join(", "),
    seo_title: articolo?.seo_title || "",
    seo_description: articolo?.seo_description || "",
    og_image: articolo?.og_image || "",
    invia_newsletter: articolo?.invia_newsletter ?? true,
  });
  const [saving, setSaving] = useState(false);
  const { toast } = useToast();

  const set = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const handleSave = async (andPublish = false) => {
    setSaving(true);
    try {
      const payload = {
        ...form,
        tags: form.tags
          .split(",")
          .map((t) => t.trim())
          .filter(Boolean),
      };
      // rimuovi slug se vuoto in create (il backend lo genera)
      if (isNew && !payload.slug) delete payload.slug;

      let saved;
      if (isNew) {
        const r = await axios.post(`${API}/api/admin/blog`, payload, { withCredentials: true });
        saved = r.data;
      } else {
        const r = await axios.put(`${API}/api/admin/blog/${articolo.id}`, payload, { withCredentials: true });
        saved = r.data;
      }
      if (andPublish && saved?.id) {
        await axios.post(`${API}/api/admin/blog/${saved.id}/publish`, {}, { withCredentials: true });
      }
      toast({ description: andPublish ? "Articolo salvato e pubblicato." : "Articolo salvato." });
      onSaved();
    } catch (e) {
      toast({
        description: "Errore: " + (e.response?.data?.detail || e.message),
        variant: "destructive",
      });
    } finally {
      setSaving(false);
    }
  };

  return (
    <div>
      <div className="flex items-center justify-between mb-6">
        <button
          onClick={onBack}
          data-testid="blog-editor-back"
          className="inline-flex items-center gap-1.5 text-slate-300 hover:text-white text-sm font-semibold"
        >
          <ArrowLeft className="h-4 w-4" /> Torna alla lista
        </button>
        <div className="flex items-center gap-2">
          <Button
            variant="secondary"
            onClick={() => handleSave(false)}
            disabled={saving || !form.titolo || !form.contenuto}
            data-testid="blog-editor-save"
          >
            <Save className="mr-2 h-4 w-4" /> Salva bozza
          </Button>
          <Button
            onClick={() => handleSave(true)}
            disabled={saving || !form.titolo || !form.contenuto}
            className="bg-[#f59e0b] hover:bg-[#d97706] text-[#0a1020] font-bold"
            data-testid="blog-editor-publish"
          >
            <Eye className="mr-2 h-4 w-4" /> Salva e pubblica
          </Button>
        </div>
      </div>

      <div className="grid lg:grid-cols-3 gap-6">
        <div className="lg:col-span-2 space-y-4">
          <Card title="Contenuto principale">
            <FormRow label="Titolo *">
              <Input value={form.titolo} onChange={(e) => set("titolo", e.target.value)} data-testid="blog-form-titolo" />
            </FormRow>
            <FormRow label="Sottotitolo">
              <Input value={form.sottotitolo} onChange={(e) => set("sottotitolo", e.target.value)} data-testid="blog-form-sottotitolo" />
            </FormRow>
            <FormRow label="Estratto (excerpt)" hint="Sommario mostrato nella lista e nella meta description se vuota.">
              <Textarea rows={2} value={form.excerpt} onChange={(e) => set("excerpt", e.target.value)} data-testid="blog-form-excerpt" />
            </FormRow>
            <FormRow label="Contenuto *" hint="Editor visuale: usa la toolbar per grassetto, corsivo, titoli, elenchi, link e immagini.">
              <div className="quill-acdt-wrapper bg-white rounded-lg overflow-hidden" data-testid="blog-form-contenuto">
                <ReactQuill
                  theme="snow"
                  value={form.contenuto}
                  onChange={(html) => set("contenuto", html)}
                  modules={QUILL_MODULES}
                  formats={QUILL_FORMATS}
                  placeholder="Scrivi qui l'articolo…"
                  style={{ minHeight: "360px" }}
                />
              </div>
            </FormRow>
          </Card>
        </div>
        <div className="space-y-4">
          <Card title="Metadati">
            <FormRow label="Slug" hint="Se vuoto viene generato dal titolo.">
              <Input value={form.slug} onChange={(e) => set("slug", e.target.value)} data-testid="blog-form-slug" placeholder="es. legge-4-2013" />
            </FormRow>
            <FormRow label="Categoria">
              <Input value={form.categoria} onChange={(e) => set("categoria", e.target.value)} data-testid="blog-form-categoria" />
            </FormRow>
            <FormRow label="Autore">
              <Input value={form.autore} onChange={(e) => set("autore", e.target.value)} data-testid="blog-form-autore" />
            </FormRow>
            <FormRow label="Tag (separati da virgola)">
              <Input value={form.tags} onChange={(e) => set("tags", e.target.value)} data-testid="blog-form-tags" placeholder="Normativa, SPID" />
            </FormRow>
            <FormRow label="Cover image URL">
              <Input value={form.cover_url} onChange={(e) => set("cover_url", e.target.value)} data-testid="blog-form-cover" />
            </FormRow>
          </Card>

          <Card title="SEO">
            <FormRow label="SEO title" hint="Se vuoto usa il titolo.">
              <Input value={form.seo_title} onChange={(e) => set("seo_title", e.target.value)} data-testid="blog-form-seo-title" />
            </FormRow>
            <FormRow label="Meta description" hint="Max ~160 caratteri.">
              <Textarea rows={3} value={form.seo_description} onChange={(e) => set("seo_description", e.target.value)} data-testid="blog-form-seo-desc" />
            </FormRow>
            <FormRow label="OG image URL" hint="Se vuoto usa la cover.">
              <Input value={form.og_image} onChange={(e) => set("og_image", e.target.value)} data-testid="blog-form-og" />
            </FormRow>
          </Card>

          <Card title="Notifiche">
            <label className="flex items-start gap-2.5 cursor-pointer group">
              <input
                type="checkbox"
                checked={form.invia_newsletter}
                onChange={(e) => set("invia_newsletter", e.target.checked)}
                data-testid="blog-form-newsletter"
                className="mt-0.5 h-4 w-4 rounded border-slate-400 bg-white/10 text-[#f59e0b] focus:ring-[#f59e0b]"
              />
              <span className="text-[13px] text-slate-200 leading-relaxed">
                <span className="font-bold text-white">Invia newsletter ai soci</span>
                <br />
                <span className="text-slate-400 text-[11.5px]">
                  Alla prima pubblicazione invia un&apos;email con anteprima a tutti gli utenti registrati.
                </span>
              </span>
            </label>
          </Card>
        </div>
      </div>
    </div>
  );
}

function Card({ title, children }) {
  return (
    <div className="rounded-2xl bg-white/5 border border-white/10 p-5">
      <div className="text-xs font-extrabold uppercase tracking-widest text-[#f59e0b] mb-4">{title}</div>
      <div className="space-y-4">{children}</div>
    </div>
  );
}

function FormRow({ label, hint, children }) {
  return (
    <div>
      <Label className="text-slate-200 text-xs font-bold uppercase tracking-wider">{label}</Label>
      <div className="mt-1.5">{children}</div>
      {hint && <div className="mt-1 text-[11px] text-slate-400">{hint}</div>}
    </div>
  );
}
